<template>
  <b-autocomplete
    v-model="query"
    :data="optionsToRender"
    :loading="isLoading"
    :placeholder="placeholder"
    :custom-formatter="formatOption"
    :disabled="disabled"
    :size="size"
    :append-to-body="appendToBody"
    class="warehouse-catalog-autocomplete"
    icon="search"
    icon-pack="fas"
    check-infinite-scroll
    open-on-focus
    clearable
    expanded
    @typing="onTyping"
    @select="onSelect"
    @infinite-scroll="onInfiniteScroll"
  >
    <template #default="props">
      <span
        class="warehouse-catalog-autocomplete__option"
        :class="{
          'warehouse-catalog-autocomplete__option--all': props.option.__isAllOption,
          'warehouse-catalog-autocomplete__option--create': props.option.__isCreateOption,
          'warehouse-catalog-autocomplete__option--wrap': appendToBody,
        }"
      >
        <template v-if="props.option.__isAllOption">{{ $t('all') }}</template>
        <template v-else-if="props.option.__isCreateOption">
          + {{ $t('add') }} "{{ props.option.__text }}"
        </template>
        <template v-else>{{ formatOption(props.option) }}</template>
      </span>
    </template>
    <template #empty>{{ $t('no_results') }}</template>
  </b-autocomplete>
</template>

<script>
import axios from 'axios'

// Contrato del endpoint: devuelve { <responseKey>: [...], lastPage } y admite ?search=&per_page=&page=.
export default {
  name: 'warehouse-catalog-autocomplete',
  props: {
    value: { type: [Number, String], default: null },
    projectId: { type: [Number, String], required: true },
    endpoint: { type: String, required: true },
    responseKey: { type: String, required: true },
    orderField: { type: String, default: 'id' },
    orderDir: { type: String, default: 'asc' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // Tamaño Buefy del campo (p. ej. 'is-small'), igual que en b-input.
    size: { type: String, default: '' },
    // Saca el desplegable al body para que flote sobre modales con scroll.
    appendToBody: { type: Boolean, default: false },
    // Cómo formatear cada opción a texto plano (para lista y campo).
    formatter: { type: Function, default: null },
    // Si viene, se usa para pintar el valor inicial en modo edición sin extra fetch.
    initialOption: { type: Object, default: null },
    // Parámetros extra que se añaden a cada petición (por ejemplo, filtrar por proveedor).
    extraParams: { type: Object, default: () => ({}) },
    // Campo del objeto seleccionado que se emite como value; útil para catálogos donde la BD guarda string.
    valueField: { type: String, default: 'id' },
    // Muestra "+ Añadir '<texto>'" al final cuando la búsqueda no coincide con ninguna opción.
    createOnMissing: { type: Boolean, default: false },
    // Devuelve el payload del POST al mismo endpoint; recibe el texto tecleado.
    createPayloadBuilder: { type: Function, default: null },
  },
  data() {
    return {
      query: '',
      options: [],
      page: 1,
      lastPage: 1,
      currentSearch: '',
      isLoading: false,
      isCreating: false,
      debounceHandle: null,
      lastSelectedValue: null,
    }
  },
  computed: {
    hasSelection() {
      return this.lastSelectedValue != null && this.lastSelectedValue !== ''
    },
    normalizedSearch() {
      return String(this.currentSearch || '').trim().toLowerCase()
    },
    // Cuando hay término y no coincide con ninguna opción existente, se ofrece crearla al vuelo.
    createOption() {
      if (!this.createOnMissing || !this.createPayloadBuilder) return null
      const term = String(this.query || '').trim()
      if (!term) return null
      const exists = (this.options || []).some((o) => {
        const label = this.formatOption(o) || ''
        return String(label).trim().toLowerCase() === term.toLowerCase()
      })
      if (exists) return null
      return { __isCreateOption: true, __text: term }
    },
    optionsToRender() {
      const base = [...this.options]
      // "Todos" solo en filtros; en alta al vuelo (createOnMissing) vaciaría un campo de formulario.
      const items = (!this.createOnMissing && !this.currentSearch && !this.hasSelection)
        ? [{ __isAllOption: true, id: null }, ...base]
        : base
      return this.createOption ? [...items, this.createOption] : items
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v == null || v === '') {
          this.query = ''
          this.lastSelectedValue = null
          return
        }
        if (v === this.lastSelectedValue) return
        this.hydrateFromValue(v)
      },
    },
    projectId() {
      this.resetAndLoad('')
    },
    extraParams: {
      deep: true,
      handler() {
        this.resetAndLoad(this.currentSearch)
      },
    },
  },
  created() {
    if (this.projectId) {
      this.resetAndLoad('')
    }
  },
  methods: {
    formatOption(option) {
      if (!option) return ''
      if (this.formatter) return this.formatter(option)
      return String(option.name || option.code || option.id || '')
    },
    extractItems(data) {
      if (data && Array.isArray(data[this.responseKey])) return data[this.responseKey]
      if (data && data.data && Array.isArray(data.data[this.responseKey])) return data.data[this.responseKey]
      return []
    },
    extractLastPage(data) {
      const raw = (data && data.lastPage != null)
        ? data.lastPage
        : (data && data.data && data.data.lastPage != null ? data.data.lastPage : 1)
      const n = Number(raw)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    buildUrl() {
      return `/api/v2/project/${this.projectId}/warehouse/${this.endpoint}`
    },
    async fetchPage(term, page) {
      const params = {
        per_page: 100,
        page,
        order_field: this.orderField,
        order_dir: this.orderDir,
        ...(this.extraParams || {}),
      }
      if (term) params.search = term
      const { data } = await axios.get(this.buildUrl(), { params })
      return { items: this.extractItems(data), lastPage: this.extractLastPage(data) }
    },
    async resetAndLoad(term) {
      if (!this.projectId) return
      this.isLoading = true
      try {
        const { items, lastPage } = await this.fetchPage(term, 1)
        this.options = items
        this.page = 1
        this.lastPage = lastPage
        this.currentSearch = term || ''
      } finally {
        this.isLoading = false
      }
    },
    onTyping(term) {
      // El value externo solo se emite al elegir de la lista; al teclear se invalida la selección previa.
      if (this.lastSelectedValue != null) {
        this.lastSelectedValue = null
        this.$emit('input', null)
      }
      clearTimeout(this.debounceHandle)
      this.debounceHandle = setTimeout(() => {
        this.resetAndLoad((term || '').trim())
      }, 300)
    },
    async onInfiniteScroll() {
      if (this.isLoading || this.page >= this.lastPage) return
      this.isLoading = true
      try {
        const nextPage = this.page + 1
        const { items, lastPage } = await this.fetchPage(this.currentSearch, nextPage)
        this.options = this.options.concat(items)
        this.page = nextPage
        this.lastPage = lastPage
      } finally {
        this.isLoading = false
      }
    },
    onSelect(option) {
      if (!option || option.__isAllOption) {
        this.lastSelectedValue = null
        this.query = ''
        this.$emit('input', null)
        this.$emit('select', null)
        return
      }
      if (option.__isCreateOption) {
        this.createFromText(option.__text)
        return
      }
      const val = option[this.valueField]
      const emitted = this.valueField === 'id' ? Number(val) : val
      this.lastSelectedValue = emitted
      this.query = this.formatOption(option)
      this.$emit('input', emitted)
      this.$emit('select', option)
    },
    async createFromText(text) {
      if (this.isCreating || !this.createPayloadBuilder) return
      this.isCreating = true
      try {
        const payload = this.createPayloadBuilder(text)
        const { data } = await axios.post(this.buildUrl(), payload)
        // El controlador puede devolver la fila creada bajo distintos alias; se recoge la primera válida.
        const created = (data && (data.taxonomy || data.provider || data.material))
          || (data && data.data && (data.data.taxonomy || data.data.provider || data.data.material))
        if (!created) return
        this.options = [...this.options, created]
        this.onSelect(created)
      } catch (e) {
        if (this.$buefy && this.$buefy.toast) {
          this.$buefy.toast.open({ message: this.$t('error'), type: 'is-danger', position: 'is-top-right' })
        }
        console.error(e)
      } finally {
        this.isCreating = false
      }
    },
    async hydrateFromValue(v) {
      const inList = (this.options || []).find((o) => o[this.valueField] === v)
      if (inList) {
        this.query = this.formatOption(inList)
        this.lastSelectedValue = v
        return
      }
      if (this.initialOption && this.initialOption[this.valueField] === v) {
        this.query = this.formatOption(this.initialOption)
        this.lastSelectedValue = v
        return
      }
      // Para valores tipo id existe filtro dedicado en materiales; para name basta con la búsqueda por texto.
      try {
        const params = { per_page: 1, page: 1 }
        if (this.valueField === 'id' && this.endpoint === 'materials') {
          params.warehouse_material_id = Number(v)
        } else {
          params.search = String(v)
        }
        const { data } = await axios.get(this.buildUrl(), { params })
        const items = this.extractItems(data)
        const found = items.find((o) => o[this.valueField] === v) || items[0]
        if (found) {
          this.query = this.formatOption(found)
          this.lastSelectedValue = v
        }
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style scoped>
.warehouse-catalog-autocomplete {
  width: 100%;
}
/* `::v-deep` va después del selector propio para conservar el atributo scoped en el ancestro
 * y alcanzar los hijos generados por Buefy; los `!important` pisan los min-width de Bulma. */
.warehouse-catalog-autocomplete ::v-deep .dropdown-menu {
  width: auto !important;
  min-width: 520px !important;
  max-width: min(92vw, 960px) !important;
}
.warehouse-catalog-autocomplete ::v-deep .dropdown-content {
  max-height: 340px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  box-shadow: 0 4px 16px rgba(10, 10, 10, 0.12);
}
.warehouse-catalog-autocomplete ::v-deep .dropdown-item {
  white-space: nowrap;
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
  line-height: 1.25;
}
.warehouse-catalog-autocomplete__option {
  display: block;
  white-space: nowrap;
}
/* En el body no aplican los estilos del menú: la opción salta de línea en vez de cortarse. */
.warehouse-catalog-autocomplete__option--wrap {
  white-space: normal;
}
.warehouse-catalog-autocomplete__option--all {
  font-weight: 600;
  color: #2b5cb8;
}
.warehouse-catalog-autocomplete__option--create {
  font-weight: 600;
  color: #22863a;
}
</style>
